import { useEffect, useMemo, useState } from 'react';
import {
  GAME_CHAT_KEY,
  gameChatMessageSchema,
  gameDocName,
  type GameChatMessage,
} from '@awwyeah/shared';
import { useYArray, useYDoc } from '../../../sync';

/**
 * Per-device "read up to here" for a game's chat: how many messages this device has seen.
 * Shared by the board's chat button and the games list, so both agree on what's new.
 */

const seenKey = (gameId: string): string => `awwyeah.games.chatSeen.${gameId}`;

export function readChatSeen(gameId: string): number {
  try {
    return Number(window.localStorage.getItem(seenKey(gameId)) ?? '0') || 0;
  } catch {
    return 0;
  }
}

export function writeChatSeen(gameId: string, n: number): void {
  try {
    window.localStorage.setItem(seenKey(gameId), String(n));
  } catch {
    // per-device nicety
  }
  try {
    window.dispatchEvent(new Event(SEEN_EVENT));
  } catch {
    // no window (tests)
  }
}

const SEEN_EVENT = 'awwyeah-chat-seen';

export function parseChat(raw: readonly unknown[]): GameChatMessage[] {
  const out: GameChatMessage[] = [];
  for (const m of raw) {
    const r = gameChatMessageSchema.safeParse(m);
    if (r.success) out.push(r.data);
  }
  return out;
}

/** Messages from someone else that arrived after `seen`. Your own lines never count. */
export function unreadFromOthers(
  chat: readonly GameChatMessage[],
  seen: number,
  meId: string,
): GameChatMessage[] {
  return chat.slice(seen).filter((m) => m.authorId !== meId);
}

/** For the games list: the newest unseen message from the other side, if any. */
export function useUnreadChat(
  gameId: string,
  meId: string,
): { count: number; latest: GameChatMessage | null } {
  const doc = useYDoc(gameDocName(gameId));
  const { items } = useYArray<unknown>(doc, GAME_CHAT_KEY);
  // Re-read the stored "seen" mark when any board marks its chat read (or another tab does).
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = (): void => setTick((n) => n + 1);
    window.addEventListener(SEEN_EVENT, bump);
    window.addEventListener('storage', bump);
    return () => {
      window.removeEventListener(SEEN_EVENT, bump);
      window.removeEventListener('storage', bump);
    };
  }, []);
  return useMemo(() => {
    const unread = unreadFromOthers(parseChat(items), readChatSeen(gameId), meId);
    return { count: unread.length, latest: unread[unread.length - 1] ?? null };
  }, [items, gameId, meId, tick]);
}
